import { Dispatch, SetStateAction } from 'react'

declare const performance: {
  memory?: {
    usedJSHeapSize: number
    jsHeapSizeLimit: number
  }
  now: () => number
}

export type PerformanceStats = {
  fps: { current: number; min: number; max: number }
  ms: { current: number; min: number; max: number }
  memory: { current: number; min: number; max: number; limit: number } | undefined
}

export class Performance {
  private frames: number
  private startTime: number
  private prevTime: number
  private listener?: Dispatch<SetStateAction<PerformanceStats>>

  private memory: { current: number; min: number; max: number; limit: number } | undefined
  private fps: { current: number; min: number; max: number }
  private ms: { current: number; min: number; max: number }

  constructor() {
    this.startTime = performance.now()
    this.prevTime = this.startTime
    this.frames = 0

    this.fps = { current: 0, min: Infinity, max: 0 }
    this.ms = { current: 0, min: Infinity, max: 0 }

    if (performance.memory) {
      this.memory = {
        current: performance.memory.usedJSHeapSize / 1048576,
        min: Infinity,
        max: 0,
        limit: performance.memory.jsHeapSizeLimit / 1048576,
      }
    }
  }

  public begin() {
    this.startTime = performance.now()
  }

  public end() {
    this.frames++
    const time = performance.now()

    const currentMs = time - this.startTime
    this.ms = {
      current: currentMs,
      min: Math.min(this.ms.min, currentMs) > 0 ? Math.min(this.ms.min, currentMs) : this.ms.min,
      max: Math.max(this.ms.max, currentMs),
    }

    if (time >= this.prevTime + 1000) {
      const currentFps = Math.round((this.frames * 1000) / (time - this.prevTime))
      this.fps = {
        current: currentFps,
        min: Math.min(this.fps.min, currentFps) > 0 ? Math.min(this.fps.min, currentFps) : this.fps.min,
        max: Math.max(this.fps.max, currentFps),
      }

      this.prevTime = time
      this.frames = 0

      if (performance.memory && this.memory) {
        const currentMemory = performance.memory.usedJSHeapSize / 1048576
        this.memory = {
          ...this.memory,
          current: currentMemory,
          min:
            Math.min(this.memory.min, currentMemory) > 0 ? Math.min(this.memory.min, currentMemory) : this.memory.min,
          max: Math.max(this.memory.max, currentMemory),
        }
      }
    }

    this.notifyListener()
  }

  public getStats() {
    return {
      fps: this.fps,
      ms: this.ms,
      memory: this.memory,
    }
  }

  public reset() {
    this.fps = { current: 0, min: Infinity, max: 0 }
    this.ms = { current: 0, min: Infinity, max: 0 }

    if (this.memory && performance.memory) {
      this.memory = {
        ...this.memory,
        current: performance.memory.usedJSHeapSize / 1048576,
        min: Infinity,
        max: 0,
      }
    }

    this.notifyListener()
  }

  public setListener(listener: Dispatch<SetStateAction<PerformanceStats>>) {
    this.listener = listener
  }

  private notifyListener() {
    if (this.listener) {
      this.listener(() => this.getStats())
    }
  }
}
